import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { CubeIcon, PlusIcon, XMarkIcon, QrCodeIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { DatePicker } from './ui/date-picker';
import { Asset, Spec, CLASES, AREAS, ESTADOS, pad2 } from '../pages/assetsShared';

const empty = { clase: 'EQ', area: 'LAV', correlativo: '', description: '', marca: '', modelo: '', ubicacionFisica: '', estado: 'operativo', fechaAlta: '', responsable: '', fichaDocId: '', nroSerie: '', proveedor: '', fechaCompra: '', garantia: '', notes: '' };

interface Props { open: boolean; editAsset: Asset | null; onClose: () => void; onSaved: () => void; }

const AssetFormModal: React.FC<Props> = ({ open, editAsset, onClose, onSaved }) => {
  const [createdId, setCreatedId] = useState<string | null>(null);
  const editId = editAsset?.id || createdId;
  const [form, setForm] = useState({ ...empty });
  const [specs, setSpecs] = useState<Spec[]>([]);
  const [foto, setFoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fotoRef = useRef<HTMLInputElement>(null);
  const [editToken, setEditToken] = useState('');
  const [formQrUrl, setFormQrUrl] = useState('');
  const [docs, setDocs] = useState<{ id: string; code?: string; title: string; tipo?: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const docRef = useRef<HTMLInputElement>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [fichaName, setFichaName] = useState('');

  // Áreas válidas para documentos (las de equipos coinciden salvo 'GRL', que no existe como área de doc)
  const MANUAL_AREAS = ['ADM', 'CAL', 'COM', 'DET', 'EQ', 'GOM', 'LAV', 'LUB', 'MEC', 'RH'];

  useEffect(() => {
    axios.get('/api/manuals?status=vigente').then(r => setDocs((r.data.manuals || []).filter((d: any) => d.tipo === 'EQ'))).catch(() => {});
    axios.get('/api/assets/users-list').then(r => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setFormQrUrl(''); setFoto(null); setCreatedId(null);
    if (editAsset) {
      setEditToken(editAsset.qrToken || '');
      setForm({
        clase: editAsset.clase, area: editAsset.area, correlativo: String(editAsset.correlativo), description: editAsset.description,
        marca: editAsset.marca || '', modelo: editAsset.modelo || '', ubicacionFisica: editAsset.ubicacionFisica || '',
        estado: editAsset.estado, fechaAlta: editAsset.fechaAlta || '', responsable: editAsset.responsable || '',
        fichaDocId: editAsset.fichaDocId || '', nroSerie: editAsset.nroSerie || '', proveedor: editAsset.proveedor || '',
        fechaCompra: editAsset.fechaCompra || '', garantia: editAsset.garantia || '', notes: editAsset.notes || '',
      });
      setSpecs(editAsset.specs || []);
    } else {
      setEditToken(''); setForm({ ...empty }); setSpecs([]);
    }
  }, [open, editAsset]);

  // Sugerir correlativo al cambiar clase/área en alta nueva
  useEffect(() => {
    if (!open || editId || !form.clase || !form.area) return;
    let cancel = false;
    axios.get(`/api/assets/next-correlativo?clase=${form.clase}&area=${form.area}`)
      .then(r => { if (!cancel) setForm(f => ({ ...f, correlativo: String(r.data.correlativo) })); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [open, editId, form.clase, form.area]);

  // Mantener el campo "Nombre de la ficha" sincronizado con el documento vinculado
  useEffect(() => {
    const d = docs.find(x => x.id === form.fichaDocId);
    setFichaName(d?.title || '');
  }, [form.fichaDocId, docs]);

  const genFormQr = async (tok = editToken) => {
    if (!tok) return;
    try { setFormQrUrl(await QRCode.toDataURL(`${window.location.origin}/activo/${tok}`, { width: 300, margin: 1 })); }
    catch { toast.error('No se pudo generar el QR'); }
  };

  // Genera el QR para subir la foto desde el celular. En alta nueva, primero
  // guarda el equipo (necesita existir para tener token) y sigue en modo edición.
  const handleQr = async () => {
    if (editId && editToken) { genFormQr(editToken); return; }
    const asset = await persist();
    if (!asset) return;
    setCreatedId(asset.id);
    setEditToken(asset.qrToken || '');
    onSaved();
    toast.success('Equipo creado. Escaneá el QR para subir la foto desde el celular.');
    genFormQr(asset.qrToken || '');
  };

  // Subir un PDF/imagen como documento tipo EQ y vincularlo al equipo en un paso
  const uploadFichaDoc = async (file: File) => {
    setUploadingDoc(true);
    try {
      const code = `${form.clase}-${form.area}-${form.correlativo ? pad2(Number(form.correlativo)) : ''}`;
      const area = MANUAL_AREAS.includes(form.area) ? form.area : 'EQ';
      const title = fichaName.trim() || `Ficha técnica ${code}${form.description ? ' · ' + form.description : ''}`.trim();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', 'EQ');
      fd.append('area', area);
      fd.append('revision', '0');
      fd.append('title', title);
      fd.append('description', '');
      fd.append('classification', 'uso_interno');
      fd.append('visibleToRoles', '[]');
      const r = await axios.post('/api/manuals', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const doc = r.data;
      setDocs(d => [{ id: doc.id, code: doc.code, title: doc.title, tipo: 'EQ' }, ...d.filter(x => x.id !== doc.id)]);
      setForm(f => ({ ...f, fichaDocId: doc.id }));
      toast.success('Ficha técnica subida y vinculada');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al subir la ficha técnica');
    } finally {
      setUploadingDoc(false);
      if (docRef.current) docRef.current.value = '';
    }
  };

  // Renombrar la ficha ya vinculada (al salir del campo)
  const renameFichaDoc = async () => {
    if (!form.fichaDocId) return;
    const d = docs.find(x => x.id === form.fichaDocId);
    const newTitle = fichaName.trim();
    if (!d || !newTitle || newTitle === d.title) return;
    try {
      await axios.put(`/api/manuals/${form.fichaDocId}`, { title: newTitle });
      setDocs(list => list.map(x => x.id === form.fichaDocId ? { ...x, title: newTitle } : x));
      toast.success('Nombre de la ficha actualizado');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'No se pudo renombrar la ficha');
    }
  };

  // Guarda el equipo (POST en alta, PUT si ya existe) y devuelve el asset o null.
  const persist = async (): Promise<Asset | null> => {
    if (!form.description.trim()) { toast.error('La descripción es obligatoria'); return null; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (k !== 'correlativo' || !codigoBloqueado) fd.append(k, v as string); });
      fd.append('specs', JSON.stringify(specs.filter(s => s.label.trim() || s.value.trim())));
      if (foto) fd.append('foto', foto);
      const r = editId
        ? await axios.put(`/api/assets/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        : await axios.post('/api/assets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data as Asset;
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error al guardar'); return null; }
    finally { setSaving(false); }
  };

  const save = async () => {
    const wasEditing = !!editId;
    const asset = await persist();
    if (!asset) return;
    toast.success(wasEditing ? 'Equipo actualizado' : 'Equipo creado');
    onSaved(); onClose();
  };

  // El codigo (clase/area/Nº) se congela cuando la ficha deja de ser borrador:
  // ahi la etiqueta QR ya esta pegada al equipo.
  const codigoBloqueado = !!editId && form.estado !== 'borrador';

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editId ? 'Editar equipo' : 'Nuevo equipo'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Clase</Label>
              <Select value={form.clase} onValueChange={v => setForm(f => ({ ...f, clase: v }))} disabled={codigoBloqueado}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CLASES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Área</Label>
              <Select value={form.area} onValueChange={v => setForm(f => ({ ...f, area: v }))} disabled={codigoBloqueado}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nº</Label>
              <Input type="number" min={1} max={99} value={form.correlativo} onChange={e => setForm(f => ({ ...f, correlativo: e.target.value }))} placeholder="auto" disabled={codigoBloqueado} />
            </div>
            <div className="space-y-1.5">
              <Label>Estado</Label>
              <Select value={form.estado} onValueChange={v => setForm(f => ({ ...f, estado: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Código: <span className="font-mono font-medium text-foreground">{form.clase}-{form.area}-{form.correlativo ? pad2(Number(form.correlativo)) : '··'}</span>
            {codigoBloqueado && <span className="ml-2">· el código se puede cambiar solo mientras la ficha está en borrador</span>}
          </div>

          <div className="space-y-1.5"><Label>Descripción *</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Aspiradora seco/húmedo industrial..." /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Marca</Label><Input value={form.marca} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} placeholder="WADFOW" /></div>
            <div className="space-y-1.5"><Label>Modelo</Label><Input value={form.modelo} onChange={e => setForm(f => ({ ...f, modelo: e.target.value }))} placeholder="WVR5A60-4" /></div>
          </div>

          <div className="space-y-1.5">
            <Label>Ficha técnica (especificaciones)</Label>
            <div className="space-y-2">
              {specs.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={s.label} onChange={e => setSpecs(sp => sp.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Dato (ej. Potencia)" className="flex-1" />
                  <Input value={s.value} onChange={e => setSpecs(sp => sp.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder="Valor (ej. 1200 W)" className="flex-1" />
                  <Button variant="ghost" size="icon" className="size-9 shrink-0" onClick={() => setSpecs(sp => sp.filter((_, j) => j !== i))}><XMarkIcon className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSpecs(sp => [...sp, { label: '', value: '' }])}><PlusIcon className="h-4 w-4" /> Agregar dato</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Ubicación física (opcional)</Label><Input value={form.ubicacionFisica} onChange={e => setForm(f => ({ ...f, ubicacionFisica: e.target.value }))} placeholder="Box 2, Estante A..." /></div>
            <div className="space-y-1.5">
              <Label>Responsable (opcional)</Label>
              <Select value={form.responsable || 'none'} onValueChange={v => setForm(f => ({ ...f, responsable: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fecha de alta</Label><DatePicker value={form.fechaAlta} onChange={v => setForm(f => ({ ...f, fechaAlta: v }))} /></div>
            <div className="space-y-1.5">
              <Label>Ficha técnica (documento)</Label>
              <div className="flex gap-2">
                <Select value={form.fichaDocId || 'none'} onValueChange={v => setForm(f => ({ ...f, fichaDocId: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Sin vincular" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin vincular</SelectItem>
                    {docs.map(d => <SelectItem key={d.id} value={d.id}>{d.code ? `${d.code} · ` : ''}{d.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input ref={docRef} type="file" accept=".pdf,image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadFichaDoc(f); }} />
                <Button type="button" variant="outline" className="gap-1.5 shrink-0" onClick={() => docRef.current?.click()} disabled={uploadingDoc}>
                  <ArrowUpTrayIcon className="h-4 w-4" /> {uploadingDoc ? 'Subiendo...' : 'Subir PDF'}
                </Button>
              </div>
              <Input value={fichaName} onChange={e => setFichaName(e.target.value)} onBlur={renameFichaDoc} placeholder="Nombre de la ficha (opcional)" />
              <p className="text-xs text-muted-foreground">Poné el nombre antes de subir el PDF, o editalo acá para renombrar la ficha vinculada.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label>Nº serie</Label><Input value={form.nroSerie} onChange={e => setForm(f => ({ ...f, nroSerie: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Proveedor</Label><Input value={form.proveedor} onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Fecha compra</Label><DatePicker value={form.fechaCompra} onChange={v => setForm(f => ({ ...f, fechaCompra: v }))} /></div>
            <div className="space-y-1.5"><Label>Garantía</Label><Input value={form.garantia} onChange={e => setForm(f => ({ ...f, garantia: e.target.value }))} placeholder="12 meses" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Foto (opcional)</Label>
            <input ref={fotoRef} type="file" accept="image/*" className="hidden" onChange={e => setFoto(e.target.files?.[0] || null)} />
            <div onClick={() => fotoRef.current?.click()} className="flex items-center gap-3 border border-dashed border-border rounded-md px-4 py-3 cursor-pointer hover:bg-muted/50">
              <CubeIcon className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground">{foto ? foto.name : 'Seleccionar foto del equipo'}</span>
            </div>
            <div className="pt-1">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleQr} disabled={saving}>
                <QrCodeIcon className="h-4 w-4" /> {saving ? 'Guardando...' : 'Subir desde el celular (QR)'}
              </Button>
              {!editId && (
                <p className="text-xs text-muted-foreground pt-1">Al generar el QR se guarda el equipo; después escaneá con el celular para subir la foto.</p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Guardando...' : (editId ? 'Guardar' : 'Crear equipo')}</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!formQrUrl} onOpenChange={o => { if (!o) setFormQrUrl(''); }}>
        <DialogContent className="w-[95vw] max-w-xs">
          <DialogHeader><DialogTitle>Subir foto desde el celular</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-2 py-2">
            <img src={formQrUrl} alt="QR subir foto" className="w-56 h-56" />
            <p className="text-xs text-muted-foreground text-center">Escaneá con el celular, subí la foto y recargá el equipo para verla.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormQrUrl('')}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AssetFormModal;
